import { Link, useNavigate } from "react-router";
import { useState } from "react";
import {
  Video, Plus, Calendar, Users, BarChart3, Radio,
  Copy, Settings, Repeat, FileText,
} from "lucide-react";
import { SkeletonCard } from "@/components/skeleton";
import { useWebinarList } from "@/hooks/use-webinar-list";
import { apiFetch, ApiError } from "@/lib/api-client";
import { formatMonthDay } from "@/lib/i18n";
import type { WebinarSummary } from "@/hooks/use-webinar-list";
import { CreateWebinarModal, type CreateWebinarPayload } from "./create-webinar-modal";

const STATUS_CONFIG: Record<WebinarSummary["status"], { label: string; style: string; pulse?: boolean }> = {
  draft: { label: "Draft", style: "bg-[var(--color-surface)] text-[var(--color-muted-foreground)] border-[var(--color-border)]" },
  scheduled: { label: "Scheduled", style: "bg-[var(--color-primary-light)] text-[var(--color-primary)] border-[var(--color-primary-border)]" },
  live: { label: "LIVE", style: "bg-[var(--color-success)]/10 text-[var(--color-success)] border-[var(--color-success)]/30", pulse: true },
  ended: { label: "Ended", style: "bg-[var(--color-surface)] text-[var(--color-muted-foreground)] border-[var(--color-border)]" },
  on_demand: { label: "On Demand", style: "bg-[var(--color-ai-light)] text-[var(--color-ai)] border-[var(--color-ai-border)]" },
  cancelled: { label: "Cancelled", style: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)] border-[var(--color-destructive)]/20" },
};

export default function WebinarListPage() {
  const navigate = useNavigate();
  const { data: webinars, isLoading, error, refetch } = useWebinarList();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Webinars</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Manage your webinars, track attendees, and review analytics.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setSubmitError(null);
            setShowCreateModal(true);
          }}
          className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] hover:bg-[var(--color-primary-hover)] transition-colors"
        >
          <Plus className="h-4 w-4" /> New Webinar
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error}
          <button onClick={refetch} className="ml-2 underline">Retry</button>
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <SkeletonCard key={i} className="rounded-xl border-[var(--color-border)] p-5" />
          ))}
        </div>
      ) : !webinars || webinars.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] py-16">
          <Video className="h-12 w-12 text-[var(--color-muted-foreground)] mb-4" />
          <h2 className="text-lg font-medium text-[var(--color-foreground)]">No webinars yet</h2>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Create your first webinar to engage your audience live.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {webinars.map((w) => (
            <WebinarCard key={w.id} webinar={w} />
          ))}
        </div>
      )}

      {showCreateModal ? (
        <CreateWebinarModal
          isSubmitting={isSubmitting}
          submitError={submitError}
          onClose={() => {
            if (!isSubmitting) {
              setShowCreateModal(false);
              setSubmitError(null);
            }
          }}
          onSubmit={async (payload) => {
            setIsSubmitting(true);
            setSubmitError(null);
            try {
              const response = await apiFetch<{ data: { id: string } }>("/v1/webinars", {
                method: "POST",
                body: JSON.stringify(payload),
              });
              setShowCreateModal(false);
              refetch();
              navigate(`/webinars/${response.data.id}/control`);
            } catch (error) {
              const message = error instanceof ApiError
                ? `${error.status}: ${error.message}`
                : error instanceof Error
                  ? error.message
                  : "Unable to create webinar";
              setSubmitError(message);
            } finally {
              setIsSubmitting(false);
            }
          }}
        />
      ) : null}
    </div>
  );
}

function WebinarCard({ webinar }: { webinar: WebinarSummary }) {
  const status = STATUS_CONFIG[webinar.status];

  return (
    <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 hover:shadow-md transition-shadow">
      {/* Status badge */}
      <div className="flex items-center justify-between mb-3">
        <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${status.style}`}>
          {status.pulse && <Radio className="h-2.5 w-2.5 animate-pulse" />}
          {status.label}
        </span>
        {webinar.isEvergreen && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-[var(--color-ai)] bg-[var(--color-ai-light)] px-1.5 py-0.5 rounded border border-[var(--color-ai-border)]">
            <Repeat className="h-2.5 w-2.5" /> Evergreen
          </span>
        )}
      </div>

      {/* Title */}
      <Link
        to={`/webinars/${webinar.id}/control`}
        className="text-sm font-semibold text-[var(--color-foreground)] hover:text-[var(--color-primary)] line-clamp-2 block"
      >
        {webinar.title}
      </Link>

      {/* Meta */}
      <div className="flex items-center gap-3 mt-2 text-xs text-[var(--color-muted-foreground)]">
        {webinar.scheduledAt && (
          <span className="flex items-center gap-0.5">
            <Calendar className="h-3 w-3" />
            {formatMonthDay(webinar.scheduledAt)}
          </span>
        )}
        <span className="flex items-center gap-0.5">
          <Users className="h-3 w-3" />
          {webinar.attendeeCount}/{webinar.registrationCount}
        </span>
      </div>

      {/* Quick actions */}
      <div className="flex items-center gap-1 mt-3 pt-3 border-t border-[var(--color-border)]">
        <Link
          to={`/webinars/${webinar.id}/analytics`}
          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)] transition-colors"
        >
          <BarChart3 className="h-3 w-3" /> Analytics
        </Link>
        <Link
          to={`/webinars/${webinar.id}/report`}
          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)] transition-colors"
        >
          <FileText className="h-3 w-3" /> Report
        </Link>
        <Link
          to={`/webinars/${webinar.id}/surveys`}
          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)] transition-colors"
        >
          <BarChart3 className="h-3 w-3" /> Surveys
        </Link>
        <Link
          to={`/webinars/${webinar.id}/offers`}
          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)] transition-colors"
        >
          <FileText className="h-3 w-3" /> Offers
        </Link>
        <button className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)] transition-colors ml-auto" aria-label="Duplicate webinar">
          <Copy className="h-3 w-3" />
        </button>
        <Link
          to={`/webinars/${webinar.id}/evergreen`}
          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)] transition-colors"
          aria-label="Webinar settings"
        >
          <Settings className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}
